'use client';

import { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { ManualControlHold } from '@/types/coordination';
import type { AlarmEntry } from './alarmTypes';
import { batteryPercent } from '@/lib/drone-telemetry';
import { manualHoldMark, manualHoldSentence, manualHoldTag } from '@/lib/manual-hold';
import { STRIP_BOARD } from '@/lib/copy';
import {
  PanelLeftClose,
  PanelLeftOpen,
  Plane,
  Wifi,
  Battery,
  Gauge,
  ArrowUp,
} from 'lucide-react';

interface StripBoardProps {
  drones: LiveDrone[];
  alerts: AlarmEntry[];
  selectedDroneId: string | null;
  onSelectDrone: (id: string) => void;
  /**
   * Who is hand-flying what, org-wide — `useWebSocket`'s `manualControlMap`,
   * passed through untouched.
   *
   * The strip is where an operator SCANS the fleet, so it is where "somebody
   * already has this aircraft" has to be readable without clicking anything.
   * Raw holds rather than resolved ids: whether a hold still counts is decided
   * against the clock, on this board's own 1 Hz tick, so a hold whose frames
   * have stopped stops being painted here even if nothing else re-renders.
   */
  manualHolds?: ReadonlyMap<string, ManualControlHold>;
}

const NO_MANUAL_HOLDS: ReadonlyMap<string, ManualControlHold> = new Map();

type SortBucket = 'alert' | 'flying' | 'idle' | 'offline';

/**
 * A LOST LINK NOW OUTRANKS THE LAST STATUS RECEIVED, which it did not before (#338).
 *
 * `CopDroneSearch.resultState` has always ordered it this way and its comment names this function
 * as the one that diverges: "the display must not vouch for a state nothing has confirmed in a
 * minute". That divergence was survivable while `status === 'flying'` meant "last seen above 2 m",
 * because an aircraft that went silent up there probably was still up.
 *
 * It stopped being survivable when `useWebSocket` began deriving `status` from the aircraft's own
 * `is_flying` as well as the bar. `status` is frozen per frame - a telemetry frame is the only
 * thing that ever rewrites it - so a DJI ending its sortie on a landing-protection hold at ~1 m,
 * whose companion then stops publishing when `post_land` cuts RC power, pinned itself at `'flying'`
 * for the rest of the session. The board read AIR 1 with a green FLY badge at the top of the
 * operator's fleet scan, while the map showed the same aircraft grey and "· LOST", the chip said
 * LINK LOST, and the dock bay row said IN DOCK. Four surfaces, three answers.
 */
function getSortBucket(drone: LiveDrone, alertDroneIds: Set<string>): SortBucket {
  if (alertDroneIds.has(drone.drone_id)) return 'alert';
  const ageSec = (Date.now() - new Date(drone.last_seen_at).getTime()) / 1000;
  if (ageSec > 60) return 'offline';
  if (drone.status === 'flying') return 'flying';
  return 'idle';
}

const BUCKET_ORDER: Record<SortBucket, number> = { alert: 0, flying: 1, idle: 2, offline: 3 };

function getStatusLabel(bucket: SortBucket): string {
  if (bucket === 'alert') return 'ALERT';
  if (bucket === 'flying') return 'FLYING';
  if (bucket === 'idle') return 'IDLE';
  return 'OFFLINE';
}

function getStatusColor(bucket: SortBucket): string {
  if (bucket === 'alert') return 'border-l-status-red-marker';
  if (bucket === 'flying') return 'border-l-status-green-marker';
  if (bucket === 'idle') return 'border-l-status-blue-marker';
  return 'border-l-status-gray-marker';
}

function getStatusBadge(bucket: SortBucket): { bg: string; text: string } {
  if (bucket === 'alert') return { bg: 'bg-status-red/10 text-status-red-fg', text: 'ALERT' };
  if (bucket === 'flying') return { bg: 'bg-status-green/10 text-status-green-fg', text: 'FLY' };
  if (bucket === 'idle') return { bg: 'bg-status-blue/10 text-status-blue-fg', text: 'IDLE' };
  return { bg: 'bg-status-gray/15 text-status-gray-fg', text: 'OFF' };
}

/** Null is "not reported", which is grey and empty — never red at 0 %. A
 *  RemoteID beacon carries no battery at all, and painting that as a flat pack
 *  is the long-standing display bug this avoids. */
function batteryBarColor(pct: number | null): string {
  if (pct === null) return 'bg-muted';
  if (pct > 50) return 'bg-status-green-marker';
  if (pct > 20) return 'bg-status-amber-marker';
  return 'bg-status-red-marker';
}

function batteryTextColor(pct: number | null): string {
  if (pct === null) return 'text-muted-foreground';
  if (pct > 50) return 'text-status-green-fg';
  if (pct > 20) return 'text-status-amber-fg';
  return 'text-status-red-fg';
}

function timeSince(iso: string): string {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

export function StripBoard({
  drones,
  alerts,
  selectedDroneId,
  onSelectDrone,
  manualHolds = NO_MANUAL_HOLDS,
}: StripBoardProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [tick, setTick] = useState(0);

  // 1 Hz. Re-renders the board so `timeSince` advances — and, now that rows
  // carry a manual-hold badge, so a hold whose frames have stopped arriving
  // stops being painted without waiting on anything else to re-render.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const alertDroneIds = useMemo(() => {
    const ids = new Set<string>();
    for (const alert of alerts) {
      if (alert.status !== 'resolved' && alert.drone_id) {
        ids.add(alert.drone_id);
      }
    }
    return ids;
  }, [alerts]);

  /**
   * The buckets, and the AIR count taken from the same pass.
   *
   * `tick` IS A DEPENDENCY, and it has to be. `getSortBucket` reads the wall clock now that a lost
   * link outranks the last status received, and every re-render of this board is otherwise driven
   * by an inbound telemetry frame — so for the aircraft this ordering exists to catch, the one that
   * has stopped sending, nothing would ever recompute it. The board would keep it green and sorted
   * to the top for the rest of the session, which is precisely the state the reorder was for.
   *
   * The AIR count comes from here rather than from a prop for the same reason. `CopMapStage`
   * counted it in its render body with no ticker of its own, so the header could read AIR 1 over a
   * board with nothing green in it — two answers to one question, on the same strip of screen.
   * One pass, one clock, one answer.
   */
  const { sorted, airborne } = useMemo(() => {
    void tick;
    const ranked = drones
      .map((drone) => ({ drone, bucket: getSortBucket(drone, alertDroneIds) }))
      .toSorted((a, b) => {
        const order = BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket];
        return order !== 0 ? order : (b.drone.battery_percent ?? 0) - (a.drone.battery_percent ?? 0);
      });
    return {
      sorted: ranked.map((r) => r.drone),
      airborne: ranked.filter((r) => r.bucket === 'flying').length,
    };
  }, [drones, alertDroneIds, tick]);

  if (collapsed) {
    return (
      <div className="w-12 shrink-0 flex flex-col items-center gap-2 py-3 rounded-md bg-card/95 backdrop-blur-sm border border-subtle">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="size-11 flex items-center justify-center rounded-md hover:bg-accent/10 text-muted-foreground"
          aria-label="Expand strip board"
        >
          <PanelLeftOpen className="size-4" strokeWidth={1.5} />
        </button>
        <div className="flex flex-col items-center gap-1 mt-2">
          <Plane className="size-3.5 text-status-green-fg" strokeWidth={1.5} />
          <span className="font-mono text-[13px] text-status-green-fg tabular-nums">{airborne}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <Wifi className="size-3.5 text-status-blue-fg" strokeWidth={1.5} />
          <span className="font-mono text-[13px] text-status-blue-fg tabular-nums">{drones.length}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 shrink-0 flex flex-col rounded-md bg-card/95 backdrop-blur-sm border border-subtle overflow-hidden" style={{ maxHeight: '100%' }}>
      {/* Header with KPIs */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-subtle">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Plane className="size-3.5 text-status-green-fg" strokeWidth={1.5} />
            <span className="font-mono text-[13px] text-status-green-fg font-bold tabular-nums">{airborne}</span>
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{STRIP_BOARD.air}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Wifi className="size-3.5 text-status-blue-fg" strokeWidth={1.5} />
            <span className="font-mono text-[13px] text-status-blue-fg font-bold tabular-nums">{drones.length}</span>
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{STRIP_BOARD.tracked}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="p-1.5 rounded-md hover:bg-accent/10 text-muted-foreground"
          aria-label="Collapse strip board"
        >
          <PanelLeftClose className="size-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Scrollable drone list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {sorted.length === 0 ? (
          <div className="flex items-center justify-center h-full py-8 text-muted-foreground text-[13px]">
            NO DRONES ONLINE
          </div>
        ) : (
          sorted.map((drone) => {
            const bucket = getSortBucket(drone, alertDroneIds);
            const badge = getStatusBadge(bucket);
            const callsign = drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase();
            const battery = batteryPercent(drone);
            const isSelected = drone.drone_id === selectedDroneId;
            // Somebody's hands are on this aircraft. Re-resolved every tick, so
            // a hold that stops reporting stops being claimed here.
            const manual = manualHoldMark(manualHolds.get(drone.drone_id));

            return (
              <button
                key={drone.drone_id}
                type="button"
                // Selection is otherwise conveyed only by colour here and by a
                // purely visual bracket on the map canvas, neither of which a
                // screen reader can report. Matches CopDockRoster's dock rows.
                aria-pressed={isSelected}
                onClick={() => onSelectDrone(drone.drone_id)}
                className={cn(
                  'w-full text-left flex items-stretch gap-0 border-l-4 transition-colors',
                  getStatusColor(bucket),
                  isSelected
                    ? 'bg-accent/15 ring-1 ring-inset ring-primary/50'
                    : 'hover:bg-accent/50',
                )}
                style={{ height: 64 }}
              >
                <div className="flex-1 flex flex-col justify-center px-3 py-1.5 min-w-0">
                  {/* Row 1: Callsign + status badge + manual-authority badge */}
                  <div className="flex items-center gap-2">
                    <span className="callsign text-[13px] text-foreground truncate">
                      {callsign}
                    </span>
                    <span className={cn('text-[11px] px-1.5 py-0.5 rounded uppercase tracking-wider', badge.bg)}>
                      {badge.text}
                    </span>
                    {/* A hand is on this aircraft RIGHT NOW — the one fact the
                        strip was missing. Amber, because for anyone who is not
                        the holder this is a caution: RTH here pulls the
                        aircraft out of somebody's hand. Named where the row has
                        room; the name truncates before the word MANUAL does,
                        because THAT is the safety-critical half. `title` gives
                        the contract's full sentence, clock included. */}
                    {manual && (
                      <span
                        title={manualHoldSentence(manual)}
                        className="text-[11px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-status-amber/15 text-status-amber-fg truncate shrink min-w-[52px]"
                      >
                        {manualHoldTag(manual)}
                      </span>
                    )}
                  </div>

                  {/* Row 2: Battery bar + Alt + Speed + Last seen */}
                  <div className="flex items-center gap-2 mt-1.5">
                    {/* Battery */}
                    <div className="flex items-center gap-1 w-[70px] shrink-0">
                      <Battery className={cn('size-3 shrink-0', batteryTextColor(battery))} strokeWidth={1.5} />
                      <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className={cn('h-full rounded-full', batteryBarColor(battery))}
                          style={{ width: battery === null ? '0%' : `${Math.min(100, battery)}%` }}
                        />
                      </div>
                      <span className={cn('font-mono text-[13px] tabular-nums', batteryTextColor(battery))}>
                        {battery === null ? 'N/A' : `${battery}%`}
                      </span>
                    </div>

                    {/* Altitude */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <ArrowUp className="size-2.5 text-muted-foreground" strokeWidth={1.5} />
                      <span className="font-mono text-[13px] text-muted-foreground tabular-nums">
                        {drone.altitude.toFixed(0)}m
                      </span>
                    </div>

                    {/* Speed */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Gauge className="size-2.5 text-muted-foreground" strokeWidth={1.5} />
                      <span className="font-mono text-[13px] text-muted-foreground tabular-nums">
                        {drone.speed.toFixed(1)}m/s
                      </span>
                    </div>

                    {/* Time since last update */}
                    <span className="ml-auto font-mono text-[13px] text-muted-foreground tabular-nums shrink-0">
                      {timeSince(drone.last_seen_at)}
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
